import { ReviewItemFileUploaded } from "@core/domain/events/reviewItemFileUploaded";
import { ReviewUpdatedEvent } from "@core/domain/events/reviewUpdated.event";
import { EventDispatcher } from "@infrastructure/eventDispatcher/eventDispatcher";
import { io } from "socket.io-client";
import { JobStartedEvent } from "@core/domain/events/jobStarted.event";
import { JobCompletedEvent } from "@core/domain/events/jobCompleted.event";
import { JobFailedEvent } from "@core/domain/events/jobFailed.event";

export class SocketGateway {
  private reviewsSocket = io("/reviews", {
    path: "/events",
    autoConnect: false,
  });
  private jobsSocket = io("/jobs", {
    path: "/events",
    autoConnect: false,
  });
  private isInitialized = false;

  constructor(private readonly eventDispatcher: EventDispatcher) {}

  init() {
    if (this.isInitialized) return;

    this.reviewsSocket.on("reviewUpdated", ({ reviewId }: { reviewId: number }) => {
      this.eventDispatcher.dispatch(new ReviewUpdatedEvent(reviewId));
    });

    this.reviewsSocket.on(
      "reviewItemFileUploaded",
      ({ reviewId, reviewItemId }: { reviewId: number; reviewItemId: number }) => {
        this.eventDispatcher.dispatch(new ReviewItemFileUploaded(reviewId, reviewItemId));
      },
    );

    this.jobsSocket.on(
      "jobStarted",
      ({
        reviewId,
        reviewItemId,
        operation,
      }: {
        reviewId: number;
        reviewItemId: number;
        operation: string;
      }) => {
        this.eventDispatcher.dispatch(new JobStartedEvent(reviewId, reviewItemId, operation));
      },
    );

    this.jobsSocket.on(
      "jobCompleted",
      ({
        reviewId,
        reviewItemId,
        operation,
      }: {
        reviewId: number;
        reviewItemId: number;
        operation: string;
      }) => {
        this.eventDispatcher.dispatch(new JobCompletedEvent(reviewId, reviewItemId, operation));
      },
    );

    this.jobsSocket.on(
      "jobFailed",
      ({
        reviewId,
        reviewItemId,
        operation,
        error,
        attempts,
      }: {
        reviewId: number;
        reviewItemId: number;
        operation: string;
        error: string;
        attempts: number;
      }) => {
        this.eventDispatcher.dispatch(
          new JobFailedEvent(reviewId, reviewItemId, operation, error, attempts),
        );
      },
    );

    this.reviewsSocket.on("connect_error", (error: any) => {
      console.error("Reviews socket connection error:", error);
    });

    this.reviewsSocket.on("disconnect", (reason: any) => {
      console.warn("Reviews socket disconnected:", reason);
      if (reason === "io server disconnect") {
        this.reviewsSocket.connect();
      }
    });

    this.jobsSocket.on("connect_error", (error: any) => {
      console.error("Jobs socket connection error:", error);
    });

    this.jobsSocket.on("disconnect", (reason: any) => {
      console.warn("Jobs socket disconnected:", reason);
      if (reason === "io server disconnect") {
        this.jobsSocket.connect();
      }
    });

    this.reviewsSocket.connect();
    this.jobsSocket.connect();
    this.isInitialized = true;
  }

  disconnect() {
    if (this.reviewsSocket) {
      this.reviewsSocket.removeAllListeners();
      this.reviewsSocket.disconnect();
    }
    if (this.jobsSocket) {
      this.jobsSocket.removeAllListeners();
      this.jobsSocket.disconnect();
    }
    this.isInitialized = false;
  }

  reconnect() {
    this.disconnect();
    this.init();
  }

  getConnectionStatus() {
    return {
      reviews: {
        connected: this.reviewsSocket.connected,
        id: this.reviewsSocket.id,
      },
      jobs: {
        connected: this.jobsSocket.connected,
        id: this.jobsSocket.id,
      },
    };
  }
}
